import { Component, DestroyRef, OnInit } from '@angular/core';
import { AddressDTO, API, AreaCategoryDTO, AreaDTO, CompanyDTO, PolygonDTO } from 'src/app/services/api/api';
import { ActivatedRoute } from '@angular/router';
import { LineString, Point, Polygon } from 'geojson';
import { UtilService } from 'src/app/services/util.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ErrorService } from 'src/app/services/error.service';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { PermissionService } from '../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';

@Component({
  selector: 'sb-page-area-edit',
  templateUrl: './area-edit.component.html',
  styleUrls: ['./area-edit.component.scss'],
  standalone: false,
})
export class AreaEditComponent implements OnInit, ModalNavigationComponent {
  isInModalContext: boolean;

  debounceTimer: ReturnType<typeof setTimeout>;

  loading: boolean;

  polygons: Polygon[];
  lineStrings: LineString[];
  points: Point[];
  areaId: number;

  area: AreaDTO;

  wizardState = 'Map';

  form = new UntypedFormGroup({
    Name: new UntypedFormControl('', [Validators.required]),
  });
  addressPoint: any;

  get Name() {
    return this.form.get('Name');
  }

  constructor(
    private api: API,
    private activatedRoute: ActivatedRoute,
    public util: UtilService,
    private errorHandler: ErrorService,
    public modalNavigation: ModalNavigationService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.debounceTimer = null;
    this.area = null;
    this.loading = true;
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.areaId = params.areaID;
      if (this.areaId) {
        this.requestDebounced();
      } else {
        this.area = this.api.createDTO('AreaDTO');
        this.area.address = new AddressDTO();
        this.area.address.country = 'DE';
        this.loading = false;
      }
    });
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  refreshFromServer() {
    this.api
      .dm_geo_Area_retrieveById(this.areaId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (area: AreaDTO) => {
          this.area = area;
          for (const poly of this.area.polygons) {
            this.util.geo.checkInCachedPolygon(poly);
          }
          this.util.geo.getCachedPolygons(this.util.arrayToIdArray(this.area.polygons)).then((cachedPolygons: PolygonDTO[]) => {
            this.area.polygons = cachedPolygons;
            this.polygons = this.util.geo.polygonDTOArrayToPolygonArray(this.area.polygons);
            this.lineStrings = this.util.geo.lineStringDTOArrayToLineStringArray(this.area.lineStrings);
            this.points = this.util.geo.pointDTOArrayToPointArray(this.area.points);
            this.Name.setValue(area.name);
          });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  selectCategory($event: AreaCategoryDTO) {
    this.area.category = $event;

    if (this.area?.category?.company && $event?.company && this.area.category.company.id === $event.company.id) {
      this.area.company = $event.company;
    } else if (this.area?.company && !$event?.company) {
      this.area.company = null;
    } else if (this.area?.company && $event?.company && this.area?.company.id !== $event?.company.id) {
      this.area.company = $event.company;
    }

    this.changeWizardState('Map');
  }

  selectCompany($event: CompanyDTO) {
    this.area.company = $event;

    if (this.area?.company && this.area?.category?.company && this.area?.company.id !== this.area?.category?.company.id) {
      this.area.category = null;
    }

    this.changeWizardState('Map');
  }

  sliceCategory() {
    if (this.area?.category?.company && this.area?.company && this.area.category.company.id === this.area?.company.id) {
      this.area.company = null;
    }
    this.area.category = null;
  }

  sliceCompany() {
    if (this.area?.category?.company && this.area?.company && this.area.category.company.id === this.area?.company.id) {
      this.area.category = null;
    }
    this.area.company = null;
  }

  changeWizardState(state: string) {
    this.wizardState = state;
  }

  submit() {
    this.loading = true;
    this.area.polygons = this.util.geo.polygonArrayToPolygonDTOArray(this.polygons);
    this.area.lineStrings = this.util.geo.lineStringArrayToLineStringDTOArray(this.lineStrings);
    this.area.points = this.util.geo.pointArrayToPointDTOArray(this.points);
    this.area.name = this.Name.value;

    if (this.areaId) {
      this.api
        .dm_geo_Area_editArea(this.area)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: () => {
            this.util.returnToNavigation(this.util.RoutingID.areas);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    } else {
      this.api
        .dm_geo_Area_createArea(this.area)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (e: AreaDTO) => {
            if (this.isInModalContext) {
              this.modalNavigation.result(e);
            } else {
              this.util.returnToNavigation(this.util.RoutingID.areas);
            }
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }
}
